import { ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core'
import { FilesizePipe } from '../filesize.pipe'
import { MatTooltip } from '@angular/material/tooltip'
import { MatIcon } from '@angular/material/icon'
import { TranslateModule } from '@ngx-translate/core'
import { catchError, of, Subscription } from 'rxjs'
import { FileUploadType, getUploadErrorMessage, UploadsService } from '../uploads.service'
import { ToastService } from '../toast.service'
import { LoggerService } from '../logger.service'
import { MatButton, MatIconButton } from '@angular/material/button'
import { DocumentUploadInfo, getFileExtension, getFileName } from '../utils'
import { FileValidationService } from '../file-validation.service'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { DurationPipe } from '../duration.pipe'

const MAX_AGENDA_PAGES = 10
@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [FilesizePipe, MatTooltip, MatIcon, TranslateModule, MatIconButton, MatButton, MatProgressBarModule, DurationPipe],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
})
export class FileUploadComponent {
  @Input({ required: true })
  acceptedFileTypes!: string[]

  @Input()
  maxSize: number | null = null

  @Input({ required: true })
  type!: FileUploadType

  @Input()
  uploadFailedMessage = 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.UPLOAD_FAILED'

  @Output()
  fileChange = new EventEmitter<DocumentUploadInfo | null>()

  private changeDetectorRef = inject(ChangeDetectorRef)
  uploadsService = inject(UploadsService)
  toastService = inject(ToastService)
  loggerService = inject(LoggerService)
  fileValidationService = inject(FileValidationService)
  uploadSubscription: Subscription | null = null
  chosenFile: File | null = null
  isUploading = false
  uploadProgress = 0
  remainingTimeSeconds: number | null = null

  fileInputChanged(fileInputEvent: any) {
    const chosenFile = fileInputEvent.target.files[0] ?? null
    if (chosenFile === null) {
      return
    }
    const fileIsValid = this.fileValidationService.validateFile(chosenFile, this.acceptedFileTypes, this.maxSize)
    if (!fileIsValid) {
      return
    }

    this.chosenFile = chosenFile
    const extension = getFileExtension(chosenFile)
    this.isUploading = true
    this.uploadProgress = 0
    this.remainingTimeSeconds = null
    const fileName = getFileName(chosenFile)
    this.uploadSubscription = this.uploadsService
      .upload(this.type, chosenFile)
      .pipe(
        catchError((error) => {
          const errorMessage = getUploadErrorMessage(error) ?? this.uploadFailedMessage
          if (this.type === 'agenda') {
            this.toastService.show(errorMessage, { maxPages: MAX_AGENDA_PAGES })
          } else {
            this.toastService.show(errorMessage)
          }
          this.loggerService.error('Error uploading file', error)
          this.isUploading = false
          this.uploadProgress = 0
          this.remainingTimeSeconds = null
          this.chosenFile = null
          this.changeDetectorRef.detectChanges()
          return of(null)
        })
      )
      .subscribe((uploadInfo) => {
        if (uploadInfo !== null) {
          this.uploadProgress = uploadInfo.progress
          this.remainingTimeSeconds = uploadInfo.remainingTimeSeconds
          if (uploadInfo.uploadKey !== null) {
            this.fileChange.emit({ key: uploadInfo.uploadKey, extension: extension.toUpperCase(), fileName })
            this.isUploading = false
            this.uploadProgress = 0
            this.remainingTimeSeconds = null
          }
          this.changeDetectorRef.detectChanges()
        }
      })
  }

  removeFile() {
    this.chosenFile = null
    this.uploadProgress = 0
    this.remainingTimeSeconds = null
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe()
      this.uploadSubscription = null
    }
    this.fileChange.emit(null)
  }

  get acceptedFileTypesString() {
    return this.acceptedFileTypes.map((fileType) => `.${fileType}`).join(',')
  }
}
